import { useNavigate, useParams } from 'react-router-dom';
import {
  useEditInteractionMutation,
  useGetInteractionQuery,
} from 'src/api/interactions';
import { useGlobalModals } from 'src/contexts/global-modals';
import { links } from 'src/router/routes';

export type FormData = {
  name: string;
  with_leaderboard: boolean;
  with_pvo: boolean;
  with_pvo_form: boolean;
  selected_theme_id: string;
};

const validate = {
  name: (value: string) =>
    !value || value.trim() === '' ? 'Name is required' : null,
};

export const useInteractionDetails = () => {
  const { id } = useParams();
  const interactionId = Number(id);
  const navigate = useNavigate();

  const { data: interaction, isPending } =
    useGetInteractionQuery(interactionId);
  const { editInteractionMutation, isPending: isEditPending } =
    useEditInteractionMutation();
  const { openDeleteInteractionModal, openLoadingModal, closeLoadingModal } =
    useGlobalModals();

  const initialValues = {
    name: interaction?.name,
    selected_theme_id: interaction?.selected_theme_id?.toString() || '',
    with_leaderboard: interaction?.with_leaderboard,
    with_pvo: interaction?.with_pvo,
    with_pvo_form: interaction?.with_pvo_form,
  };

  const themeOptions =
    interaction?.themes.map((theme) => ({
      image: theme.image,
      label: theme.name,
      value: theme.id.toString(),
    })) || [];

  const handleCancel = () => {
    navigate(links.interactions);
  };

  const handleDelete = () => {
    openDeleteInteractionModal(interactionId);
  };

  const handleSubmit = async (values: FormData) => {
    openLoadingModal(
      'Editing interaction',
      'Please wait while we edit the interaction...'
    );
    try {
      await editInteractionMutation({
        data: {
          name: values.name,
          selected_theme_id: values.selected_theme_id
            ? parseInt(values.selected_theme_id)
            : undefined,
          with_leaderboard: values.with_leaderboard,
          with_pvo: values.with_pvo,
          with_pvo_form: values.with_pvo_form,
        },
        id: interactionId,
      });
    } catch (error) {
      console.error('Failed to edit interaction:', error);
    } finally {
      closeLoadingModal();
    }
  };

  return {
    handleDelete,
    handleSubmit,
    initialValues,
    interaction,
    isEditPending,
    isPending,
    themeOptions,
    validate,
    handleCancel,
  };
};
